"use client";

import { PortalStatusBadge } from "@/app/portal/_view/portal-status-badge";
import { useFormatDate } from "@/lib/i18n/format";
import { Send } from "lucide-react";
import { useTranslations } from "next-intl";
import type { TaskStatusPhase } from "@v1/backend/logic/types";
import type { ReactElement } from "react";
import { MessengerAgentAvatar } from "./messenger-avatars";
import { useMessenger } from "./messenger-context";

export type MessengerTicketStatus = TaskStatusPhase;

/** „#2026-1183 · In Bearbeitung“ under a thread title. */
export function MessengerTicketSubtitle({
  number,
  status,
}: {
  readonly number: string;
  readonly status: MessengerTicketStatus;
}): ReactElement {
  return (
    <>
      <span className="text-[11.5px] tabular-nums leading-tight text-primary/45">#{number}</span>
      <PortalStatusBadge status={status} />
    </>
  );
}

export function MessengerEventRow({ at }: { readonly at: number }): ReactElement {
  const t = useTranslations("messenger.thread");
  const formatDate = useFormatDate();
  return (
    <p className="px-4 py-1 text-center text-[11px] leading-tight text-primary/40">
      {t("uebergeben", { time: formatDate.time(at) })}
    </p>
  );
}

export function MessengerStatusRow({ text }: { readonly text: string }): ReactElement {
  return <p className="px-4 pb-4 text-center text-[11px] leading-tight text-primary/40">{text}</p>;
}

export function MessengerTypingBubble(): ReactElement {
  const t = useTranslations("messenger.thread");
  return (
    <li className="mt-4 flex items-end gap-2">
      <MessengerAgentAvatar size="sm" />
      <div className="rounded-2xl rounded-bl-md bg-action-500/[0.05] px-3.5 py-2 text-[14px] leading-relaxed">
        <span className="agent-planning-text-shimmer">{t("schreibt")}</span>
      </div>
    </li>
  );
}

export function MessengerFailedRow({ onHandToTeam }: { readonly onHandToTeam: (() => void) | null }): ReactElement {
  const t = useTranslations("messenger");
  return (
    <li className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-destructive/[0.04] px-3.5 py-2.5">
      <span className="text-[13px] text-destructive">{t("thread.fehlgeschlagen")}</span>
      {onHandToTeam !== null ? (
        <button
          type="button"
          onClick={onHandToTeam}
          className="text-[13px] font-medium text-action-700 hover:text-action-900"
        >
          {t("header.anTeamGeben")}
        </button>
      ) : null}
    </li>
  );
}

export function MessengerLoadOlder({ onClick }: { readonly onClick: () => void }): ReactElement {
  const t = useTranslations("messenger.lists");
  return (
    <div className="flex justify-center pt-3">
      <button
        type="button"
        onClick={onClick}
        className="rounded-full px-3 py-1 text-[12px] font-medium text-primary/55 hover:bg-action-500/[0.06]"
      >
        {t("mehrLaden")}
      </button>
    </div>
  );
}

/** Closed ticket: no composer, a way to start over. */
export function MessengerDoneBar(): ReactElement {
  const t = useTranslations("messenger");
  const { nav } = useMessenger();
  return (
    <div className="shrink-0 border-t border-border/40 bg-background px-3 py-3">
      <div className="flex min-h-11 items-center justify-between gap-3 rounded-xl bg-action-500/[0.04] py-1.5 pl-4 pr-1.5">
        <span className="text-[13px] text-primary/55">{t("thread.erledigt")}</span>
        <MessengerPrimaryButton label={t("lists.neueNachricht")} onClick={() => nav.push({ kind: "chat", sessionId: null })} />
      </div>
    </div>
  );
}

export function MessengerPrimaryButton({
  label,
  onClick,
}: {
  readonly label: string;
  readonly onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full bg-action-500 px-4 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-action-600 active:scale-[0.98]"
    >
      {label}
      <Send className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
    </button>
  );
}
